import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveTrips } from '../../services/tripService';
import toast from 'react-hot-toast';

export default function RiderDashboard() {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTrips, setActiveTrips] = useState([]);

  useEffect(() => {
    if (userProfile?.id) {
      getActiveTrips(userProfile.id, 'rider').then(setActiveTrips).catch(() => {});
    }
  }, [userProfile]);

  const handleLogout = async () => {
    try { await logout(); navigate('/login'); } catch (err) { toast.error('Logout failed'); }
  };

  const cards = [
    { icon: '🚗', title: 'Book a Ride', desc: 'Pre-book with upfront pricing', path: '/rider/book', color: '#2dd4a8' },
    { icon: '🔁', title: 'Recurring Bookings', desc: 'Manage your series', path: '/rider/series', color: '#4f46e5' },
    { icon: '🚐', title: 'Vehicle Groups', desc: 'Multi-vehicle bookings', path: '/rider/vehicle-groups', color: '#8b5cf6' },
    { icon: '💰', title: 'My Wallet', desc: 'View balance & transactions', path: '/rider/wallet', color: '#fbbf24' },
    { icon: '📋', title: 'My Trips', desc: 'View trip history', path: '/rider/trips', color: '#6366f1' },
    { icon: '🎫', title: 'Support', desc: 'Get help or report an issue', path: '/rider/support', color: '#8b5cf6' },
    { icon: '⚖️', title: 'Complaints', desc: 'Submit or track a complaint', path: '/rider/complaints', color: '#dc2626' },
    { icon: '🚗', title: 'Damage claims', desc: 'Claims against your trips', path: '/rider/claims', color: '#f59e0b' },
    { icon: '📋', title: 'Compliance', desc: 'Reservo policies', path: '/compliance', color: '#1e40af' },    
    { icon: '🔍', title: 'Lost items', desc: 'Report or track lost items', path: '/rider/lost-items', color: '#f59e0b' },    
    { icon: '📜', title: 'Legal', desc: 'Terms, privacy & agreements', path: '/legal', color: '#94a3b8' },
    { icon: '⚙️', title: 'Settings', desc: 'Account & preferences', path: '/rider/settings', color: '#5a6578' },
  ];

  return (
    <div className="max-w-[700px] mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="font-serif mt-0 mb-1 text-brand-dark">
            Welcome, {userProfile?.firstName || 'Rider'}
          </h1>
          <div className="text-[0.9rem] text-muted">Rider ID: {userProfile?.referenceNumber || '—'}</div>
        </div>
        <button onClick={handleLogout} className="px-5 py-2.5 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold text-[0.85rem] text-body">Sign Out</button>
      </div>

      {/* Active Trip Banner */}
      {activeTrips.length > 0 && (
        <div onClick={() => navigate(`/rider/trip/${activeTrips[0].id}`)} className="p-5 rounded-2xl mb-6 cursor-pointer"
          style={{ background: 'linear-gradient(135deg, #2dd4a8, #6ee7b7)', color: '#0a1628' }}>
          <div className="flex justify-between items-center">
            <div>
              <div className="font-extrabold text-[1.1rem]">Active Trip</div>
              <div className="text-[0.9rem] opacity-80">
                {activeTrips[0].referenceNumber} — {activeTrips[0].status}
              </div>
            </div>
            <span className="text-[1.5rem]">→</span>
          </div>
        </div>
      )}

      {/* Action Cards */}
      <div className="grid gap-4 mb-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {cards.map((card) => (
          <div key={card.title} onClick={() => navigate(card.path)} className="p-6 rounded-[20px] border border-slate-200 bg-white cursor-pointer text-center transition-all"
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div className="text-[2rem] mb-3">{card.icon}</div>
            <div className="font-bold text-brand-dark mb-1">{card.title}</div>
            <div className="text-[0.8rem] text-muted">{card.desc}</div>
          </div>
        ))}
      </div>

      {/* Quick Info */}
      <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200">
        <div className="text-[0.85rem] text-body leading-relaxed">
          <strong className="text-brand-dark">How Reservo works:</strong> Book a ride 1 hour to 30 days in advance.
          Choose from 4 service tiers — Go, Comfy, Plus, or VIP. Your driver will receive a unique Trip PIN
          that only you can provide, ensuring your ride is safe and verified.
        </div>
      </div>
    </div>
  );
}